import { Navigate, Route, Routes, useParams } from "react-router-dom";
import type { StaffUser } from "../../../lib/auth";
import { DOC_CONFIG, type DocKind } from "./config";
import { SalesShell } from "./SalesShell";
import { DocList } from "./DocList";
import { DocEditor } from "./DocEditor";
import { DocDetail } from "./DocDetail";
import { ItemsManager } from "./ItemsManager";
import { PaymentsList } from "./PaymentsList";
import { CardOrdersList } from "./cardOrders";
import { LogoOrdersList } from "./logoOrders";

function useValidKind(): DocKind | null {
  const { kind } = useParams<{ kind: string }>();
  return kind && kind in DOC_CONFIG ? (kind as DocKind) : null;
}

function ListRoute({ user }: { user: StaffUser }) {
  const kind = useValidKind();
  if (!kind) return <Navigate to="/sales/invoices" replace />;
  return (
    <SalesShell titleKey={DOC_CONFIG[kind].titleKey}>
      <DocList kind={kind} user={user} />
    </SalesShell>
  );
}

function EditorRoute() {
  const kind = useValidKind();
  if (!kind) return <Navigate to="/sales/invoices" replace />;
  return <DocEditor kind={kind} />;
}

function DetailRoute({ user }: { user: StaffUser }) {
  const kind = useValidKind();
  if (!kind) return <Navigate to="/sales/invoices" replace />;
  return <DocDetail kind={kind} user={user} />;
}

export function SalesSection({ user }: { user: StaffUser }) {
  return (
    <Routes>
      <Route index element={<Navigate to="/sales/invoices" replace />} />
      <Route
        path="payments"
        element={
          <SalesShell titleKey="sales.payments.title" introKey="sales.payments.intro">
            <PaymentsList user={user} />
          </SalesShell>
        }
      />
      <Route
        path="items"
        element={
          <SalesShell titleKey="sales.items.title" introKey="sales.items.intro">
            <ItemsManager user={user} />
          </SalesShell>
        }
      />
      <Route
        path="card-orders"
        element={
          <SalesShell titleKey="sales.cardOrders.title" introKey="sales.cardOrders.intro">
            <CardOrdersList />
          </SalesShell>
        }
      />
      <Route
        path="logo-orders"
        element={
          <SalesShell titleKey="sales.logoOrders.title" introKey="sales.logoOrders.intro">
            <LogoOrdersList />
          </SalesShell>
        }
      />
      <Route path=":kind" element={<ListRoute user={user} />} />
      <Route path=":kind/new" element={<EditorRoute />} />
      <Route path=":kind/:id/edit" element={<EditorRoute />} />
      <Route path=":kind/:id" element={<DetailRoute user={user} />} />
      <Route path="*" element={<Navigate to="/sales/invoices" replace />} />
    </Routes>
  );
}
